import { Box, Button, Flex, FormControl, Heading, Text, useToast } from "@chakra-ui/react";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageAreaPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const [city, setCity] = useState("")
  const { getArea, addArea, updateArea, getCities, getCountries } = useApi();
  const {
    data: area,
    isLoading,
    mutate,
  } = useCache("area", () => getArea(+id || 0));

  const initialValues = useMemo(() => {
    return {
      name: area?.name || "",
      city: area?.city_id || "",
      country: area?.country_id || "",
      submit: null,
    };
  }, [area]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Nom requis"),
    country: Yup.string().trim().required("pays requis"),
    city: Yup.string().trim().required("Commune requis"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const postData: any = {
          name: values.name,
          city_id: values.city,
          country_id: values.country,
        };
        let response: any = null;
        if (id) {
          response = await updateArea({
            id: id,
            ...postData,
          });
        } else {
          response = await addArea({
            ...postData,
          });
        }
        if (!response.error_code && !response.errors) {
          dispatch(
            showToast({
              toast: toast,
              message: id
                ? "La commune a été mise à jour avec succès."
                : "La commune a été créée avec succès.",
              status: toastTypes.success,
            })
          );
          if (id) {
            mutate(getArea(+id));
            navigate("/areas");
          } else {
            navigate(`/areas`);
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });

  const {
    data: countries,
    isLoading: isCountryLoading,
    mutate: mutateCountry,
  } = useCache("countries", () => getCountries(1, ""));

  const {
    data: cities,
    isLoading: isCityLoading,
    mutate: mutateCity,
  } = useCache("cities", () => {
    // En mode édition, utiliser le pays du area si disponible
    const countryId = values.country || (id && area?.country_id ? area.country_id : "");
    return getCities(countryId || "", 1, city || "");
  });

  // À ajouter après les useCache
  useEffect(() => {
    if (id && area?.country_id && area?.city_id) {
      // En mode édition, charger les villes du pays
      getCities(String(area.country_id), 1, "")
        .then((citiesData) => {
          mutateCity(citiesData || []);
          // Définir la ville si elle existe
          if (area.city_id && !values.city) {
            setFieldValue("city", String(area.city_id));
          }
        });
    }
  }, [id, area?.country_id, area?.city_id, mutateCity, getCities, values.city, setFieldValue]);

  // À ajouter après les useCache
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier une commune" : "Créer une commune"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                >
                </Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  {/* Commune */}
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                    <Text color="gray_description">Nom</Text>
                    <FormControl
                      isInvalid={!!errors.name && (touched.name as boolean)}
                    >
                      <AppInput
                        required
                        placeholder={`Nom`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="name"
                        name="name"
                        autoComplete="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        showErrorMessage={Boolean(touched.name && errors.name)}
                        errorMessage={errors.name}
                      />
                    </FormControl>
                  </Flex>

                  {/* Pays */}
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                    <Text color="gray_description">Pays</Text>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      width={"100%"}
                    >
                      <AppSelect
                        id="country"
                        name="country"
                        onChange={(field: string, value: any) => {
                          setFieldValue(field, value);
                          setFieldValue("city", "");
                          // Charger les villes du pays sélectionné
                          if (value) {
                            mutateCity(getCities(value || "", 1, ""));
                          } else {
                            mutateCity([]); // Vider les villes si aucun pays
                          }
                        }}
                        value={values.country || initialValues.country}
                        placeholder="Veuillez sélectionner un pays"
                        options={countries}
                        isMulti={false}
                        isLoading={isCountryLoading}
                        showErrorMessage={Boolean(
                          touched.country && errors.country
                        )}
                        errorMessage={errors.country}
                        onBlur={handleBlur}
                      />
                    </Flex>
                  </Flex>

                  {/* Ville */}
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                    <Text color="gray_description">Ville</Text>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      width={"100%"}
                    >
                      <AppSelect
                        id="city"
                        name="city"
                        handleInputChange={(value) => {
                          setCity(value)
                          mutateCity(getCities(0, 1, value));
                        }}
                        onChange={(field: string, value: any) => {
                          setFieldValue(field, value);
                          mutateCity(getCities(0, 1, value));
                        }}
                        value={values.city || initialValues.city}
                        placeholder="Veuillez sélectionner une ville"
                        options={cities}
                        isMulti={false}
                        isLoading={isCityLoading}
                        showErrorMessage={Boolean(
                          touched.city && errors.city
                        )}
                        errorMessage={errors.city}
                        onBlur={handleBlur}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
